import { setLangIso } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import React from 'react';

interface TranslatedTextProps {
    translationKey: string;
    fallback?: string;
}

function TranslatedText({ translationKey, fallback }: TranslatedTextProps) {
    // Implementation will be added later
    return <>{fallback || translationKey}</>;
}
type UITranslationKeys =
    | 'share'
    | 'shareModalTitle'
    | 'copy'
    | 'copied'
    | 'loading'
    | 'privacyChoices'
    | 'privacyPolicy'
    | 'poweredBy'
    | 'californiaNotice';

interface TranslatedUITextProps {
    translationKey: UITranslationKeys;
    fallback?: string;
}

export function TranslatedUIText({ translationKey, fallback }: TranslatedUITextProps) {
    const translation = useUITranslatedText(translationKey, fallback);
    return <>{translation}</>;
}

export default TranslatedText;

export function useTranslatedText(translationKey: string, fallback?: string) {
    const langIso = useAppSelector((state) => state.app.langIso);
    const translations = useAppSelector((state) => state.app.translations);
    const translation = translations?.[langIso]?.[translationKey];

    if (translation) {
        return translation;
    }

    if (langIso && process.env.PUBLIC_SUPPORT_TRANSLATIONS == 'true') {
        console.warn(`Translation for key "${translationKey}" not found in language "${langIso}". Using fallback.`);
    }

    return fallback || '';
}

export function useUITranslatedText(key: UITranslationKeys, fallback?: string) {
    const universeId = useAppSelector((state) => state.app.universeId);
    const fullKey = `ui-${universeId}__${key}`;
    return useTranslatedText(fullKey, fallback);
}

export function useCFTranslatedText(key: string, fallback?: string) {
    const universeId = useAppSelector((state) => state.app.universeId);
    const fullKey = `cf-${universeId}__${key}`;
    return useTranslatedText(fullKey, fallback);
}

export function useLang() {
    const translations = useAppSelector((state) => state.app.translations);
    const current = useAppSelector((state) => state.app.langIso);
    let langs = useAppSelector((state) => state.app.availableLangs);

    if (!langs) {
        langs = [];
    } else {
        langs = [...langs];
    }
    langs.unshift({
        langISO: 'en-US',
        displayName: 'English',
    }); // Ensure 'en' is always the first option

    const dispatch = useAppDispatch();
    function setLang(langIso: string) {
        dispatch(setLangIso(langIso));
    }

    return {
        current,
        langs,
        setLang,
    };
}
